/**
 * field.js —— 阵法本体
 *
 * 结构（由内到外）：
 *   taiji      中心太极，最亮，立体态时最靠前
 *   layers[12] 十二层文字环（加太极共十三层），每层为底图 + 数据高亮图
 *   particles  流动粒子，绕中心公转，立体态时向中心汇聚
 *   vortex     金色环，旋涡态才可见
 *   stars      背景星场，远平面，缓慢视差
 *
 * 这个类只管「摆出什么样子」，不管「什么时候摆」——时序在 states.js。
 * 对外暴露的是一组归一化控制量（0–1），状态机去驱动它们。
 */

import * as THREE from 'three';
import {
  drawRing, drawRingHighlights, drawTaiji, RING_SPECS, TAIJI_RADIUS
} from './ring-texture.js';
import {
  CAMERA, CAST_RITUAL, CELL_HIGHLIGHT, CENTER_GLOW, COLORS, FIELD, LIGHT, MEIHUA_VIEW,
  PARTICLES, RING_STYLE, SCAN_BAND, STARS, TAIJI_STYLE, VORTEX
} from './tuning.js';

/** 在浏览器里建画布；Node 里由调用方注入 createCanvas 以便离线渲染校对 */
function makeCanvas(size, factory) {
  if (factory) return factory(size, size);
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  return c;
}

const tokenLabel = (token) => String(token?.label ?? token);

/** 所有盘面元素共用同一条锥深曲线；圆心代入 0 时天然位于锥顶 z=0。 */
const coneDepthZ = (normalizedRadius) => -FIELD.cone
  * Math.pow(normalizedRadius, FIELD.coneExponent)
  * FIELD.outerRadius;

const RING_VERTEX_SHADER = /* glsl */`
  varying vec2 vUv;
  varying vec3 vWorldPosition;
  varying vec3 vWorldBasisX;
  varying vec3 vWorldBasisY;
  varying vec3 vWorldBasisZ;

  void main() {
    vUv = uv;
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPosition.xyz;
    vWorldBasisX = normalize((modelMatrix * vec4(1.0, 0.0, 0.0, 0.0)).xyz);
    vWorldBasisY = normalize((modelMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz);
    vWorldBasisZ = normalize((modelMatrix * vec4(0.0, 0.0, 1.0, 0.0)).xyz);
    gl_Position = projectionMatrix * viewMatrix * worldPosition;
  }
`;

const RING_FRAGMENT_SHADER = /* glsl */`
  uniform sampler2D uMap;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform vec3 uLightDirection;
  uniform vec3 uSpecColor;
  uniform float uSpecStrength;
  uniform float uShininess;
  uniform float uRingTilt;
  uniform float uAlphaCutoff;

  varying vec2 vUv;
  varying vec3 vWorldPosition;
  varying vec3 vWorldBasisX;
  varying vec3 vWorldBasisY;
  varying vec3 vWorldBasisZ;

  void main() {
    vec4 texel = texture2D(uMap, vUv);
    if (texel.a <= uAlphaCutoff) discard;

    vec2 centered = vUv - vec2(0.5);
    float angle = atan(centered.y, centered.x);
    vec3 localRingNormal = normalize(vec3(
      cos(angle) * uRingTilt,
      sin(angle) * uRingTilt,
      1.0
    ));
    vec3 worldRingNormal = normalize(
      vWorldBasisX * localRingNormal.x
      + vWorldBasisY * localRingNormal.y
      + vWorldBasisZ * localRingNormal.z
    );
    vec3 viewDirection = normalize(cameraPosition - vWorldPosition);
    float reflected = max(dot(
      reflect(-viewDirection, worldRingNormal),
      normalize(uLightDirection)
    ), 0.0);
    float specular = pow(reflected, uShininess) * uSpecStrength;
    vec3 color = texel.rgb * uColor + uSpecColor * specular;
    gl_FragColor = vec4(color, texel.a * uOpacity);
  }
`;

function makeRingMaterial(texture, spec, baseColor) {
  const radial = THREE.MathUtils.clamp(spec.radius, 0, 1);
  const tiltScale = THREE.MathUtils.lerp(
    LIGHT.innerTiltScale,
    LIGHT.outerTiltScale,
    radial
  );
  const strengthScale = THREE.MathUtils.lerp(
    LIGHT.innerStrengthScale,
    LIGHT.outerStrengthScale,
    radial
  );
  const color = baseColor.clone();
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uMap: { value: texture },
      uColor: { value: color },
      uOpacity: { value: 0 },
      uLightDirection: {
        value: new THREE.Vector3(
          LIGHT.direction.x,
          LIGHT.direction.y,
          LIGHT.direction.z
        ).normalize()
      },
      uSpecColor: { value: new THREE.Color(COLORS.highlight) },
      uSpecStrength: { value: LIGHT.specStrength * strengthScale },
      uShininess: { value: LIGHT.shininess },
      uRingTilt: { value: LIGHT.ringTilt * tiltScale },
      uAlphaCutoff: { value: LIGHT.alphaCutoff }
    },
    vertexShader: RING_VERTEX_SHADER,
    fragmentShader: RING_FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending
  });
  // 保留 MeshBasicMaterial 时代的调参接口，颜色对象与 shader uniform 共用引用。
  material.color = color;
  material.opacity = 0;
  return material;
}

export class Field {
  /**
   * @param {object} o
   *   canvasFactory  可选，(w,h)=>canvas，Node 下传 node-canvas 的 createCanvas
   *   textureSize    贴图边长
   *   particleCount / starCount  可覆盖 tuning 的默认
   */
  constructor(o = {}) {
    const {
      canvasFactory,
      textureSize = FIELD.textureSize,
      particleCount = PARTICLES.count,
      starCount = STARS.count
    } = o;

    this.root = new THREE.Group();
    this.setOrientation({});
    this.canvasFactory = canvasFactory;
    this.textureSize = textureSize;
    this.highlightTextureSize = Math.round(textureSize * FIELD.highlightTextureScale);
    this._disposables = [];
    this.mode = 'liuyao';
    this._interactiveCells = [];
    this._flyingStarInteractiveCells = [];
    this._lastCellMap = {};
    this.performanceTier = 0;
    this._qualityHiddenLayers = new Set();
    this._raycaster = new THREE.Raycaster();
    this._pointer = new THREE.Vector2();
    this._staticRingColor = new THREE.Color(COLORS.jade).lerp(
      new THREE.Color(COLORS.jadeBright),
      RING_STYLE.materialBrightMix
    );
    this._castRingColor = new THREE.Color(COLORS.jadeBright);
    this._movingRingColor = new THREE.Color(COLORS.crimson);
    this._ringSnapActive = false;
    this._ringsSnapped = false;

    /** 控制量，全部 0–1，由状态机写入 */
    this.control = {
      awaken: 0,      // 阵眼亮度
      expand: 0,      // 平面展开度
      depth: 0,       // 立体化程度
      vortex: 0,      // 旋涡强度
      shock: 0,       // 冲击波（一次性脉冲）
      castWave: 1,    // 落爻定格环波，0=刚触发，1=消散
      completionPulse: 0, // 六爻满后的全盘外推与太极强闪
      taijiFlash: 0,  // 展开完成时只闪阵眼，不推动整盘
      ringSnap: 0,    // 成卦归位进度，0=当前角，1=真实方位
      ringBaseOpacity: 0, // 兼容梅花模式；六爻 AIM 始终为 0
      spinMultiplier: 0,  // 各状态对每层 idleSpin 的统一倍率
      layerReveal: RING_SPECS.map(() => 0),
      layerScale: RING_SPECS.map(() => RING_STYLE.revealScaleMin),
      highlightReveal: RING_SPECS.map(() => 0)
    };

    /** 每层的动爻标记，成卦后由 reveal() 写入 */
    this.movingLayers = new Set();

    this._buildStars(starCount);
    this._buildCenterGlow();
    this._buildLayers();
    this._buildCastWave();
    this._buildScanBand();
    this._buildTaiji();
    this._buildParticles(particleCount);
    this._buildVortex();

    this._t = 0;
  }

  /* ------------------------------------------------------- 构建 */

  _buildCenterGlow() {
    const canvas = makeCanvas(CENTER_GLOW.textureSize, this.canvasFactory);
    const ctx = canvas.getContext('2d');
    const center = CENTER_GLOW.textureSize / 2;
    const gradient = ctx.createRadialGradient(center, center, 0, center, center, center);
    const inner = new THREE.Color(COLORS.glowInner).getStyle(THREE.SRGBColorSpace);
    const mid = new THREE.Color(COLORS.glowMid).getStyle(THREE.SRGBColorSpace);
    gradient.addColorStop(0, inner);
    gradient.addColorStop(CENTER_GLOW.midStop, mid);
    gradient.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, CENTER_GLOW.textureSize, CENTER_GLOW.textureSize);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    this.centerGlowMaterial = new THREE.SpriteMaterial({
      map: texture,
      color: COLORS.taiji,
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this.centerGlow = new THREE.Sprite(this.centerGlowMaterial);
    const diameter = FIELD.outerRadius * CENTER_GLOW.radiusScale * 2;
    this._centerGlowDiameter = diameter;
    this.centerGlow.scale.set(diameter, diameter, 1);
    this.centerGlow.position.z = coneDepthZ(0) + CENTER_GLOW.depthOffset;
    this.centerGlow.renderOrder = CENTER_GLOW.renderOrder;
    this.root.add(this.centerGlow);
    this._disposables.push(texture, this.centerGlowMaterial);
  }

  _buildLayers() {
    this.layers = RING_SPECS.map((sourceSpec, i) => {
      // 飞星环会在运行时换 token，每个 Field 必须拥有独立 spec，
      // 不得污染全局 RING_SPECS 及其他离线渲染实例。
      const spec = { ...sourceSpec, tokens: [...sourceSpec.tokens] };
      const canvas = makeCanvas(this.textureSize, this.canvasFactory);
      const ctx = canvas.getContext('2d');
      drawRing({
        ctx,
        size: this.textureSize,
        tokens: spec.tokens,
        inner: spec.inner,
        outer: spec.outer,
        offset: spec.offset,
        fontScale: spec.fontScale,
        textOpacity: spec.textOpacity,
        weight: spec.weight,
        glow: this.textureSize * RING_STYLE.glowRatio
      });

      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = RING_STYLE.textureAnisotropy;

      const staticHighlightCanvas = CELL_HIGHLIGHT.layerKeys.includes(spec.key)
        ? makeCanvas(this.highlightTextureSize, this.canvasFactory)
        : null;
      const pulseHighlightCanvas = CELL_HIGHLIGHT.pulseLayerKeys.includes(spec.key)
        ? makeCanvas(this.highlightTextureSize, this.canvasFactory)
        : null;
      const staticHighlightTexture = staticHighlightCanvas
        ? new THREE.CanvasTexture(staticHighlightCanvas)
        : null;
      const pulseHighlightTexture = pulseHighlightCanvas
        ? new THREE.CanvasTexture(pulseHighlightCanvas)
        : null;
      for (const overlayTexture of [staticHighlightTexture, pulseHighlightTexture].filter(Boolean)) {
        overlayTexture.colorSpace = THREE.SRGBColorSpace;
        overlayTexture.anisotropy = RING_STYLE.textureAnisotropy;
      }

      const material = makeRingMaterial(
        texture,
        spec,
        spec.preserveTextureColors ? new THREE.Color(COLORS.taiji) : this._staticRingColor
      );

      const makeHighlightMaterial = (map) => map ? new THREE.MeshBasicMaterial({
        map,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        opacity: 0,
        color: COLORS.taiji
      }) : null;
      const staticHighlightMaterial = makeHighlightMaterial(staticHighlightTexture);
      const pulseHighlightMaterial = makeHighlightMaterial(pulseHighlightTexture);

      // 贴图里环外缘在 spec.outer，要让它落到 radius * outerRadius
      const worldSize = (FIELD.outerRadius * spec.radius) / spec.outer;
      const geometry = new THREE.PlaneGeometry(worldSize, worldSize);
      const baseMesh = new THREE.Mesh(geometry, material);
      const staticHighlightMesh = staticHighlightMaterial
        ? new THREE.Mesh(geometry, staticHighlightMaterial)
        : null;
      const pulseHighlightMesh = pulseHighlightMaterial
        ? new THREE.Mesh(geometry, pulseHighlightMaterial)
        : null;
      baseMesh.renderOrder = 10 + i;
      if (staticHighlightMesh) {
        staticHighlightMesh.position.z = CELL_HIGHLIGHT.depthOffset;
        staticHighlightMesh.renderOrder = CELL_HIGHLIGHT.renderOrderBase + i;
      }
      if (pulseHighlightMesh) {
        pulseHighlightMesh.position.z = CELL_HIGHLIGHT.pulseDepthOffset;
        pulseHighlightMesh.renderOrder = CELL_HIGHLIGHT.renderOrderBase + RING_SPECS.length + i;
      }

      const mesh = new THREE.Group();
      mesh.add(baseMesh);
      if (staticHighlightMesh) mesh.add(staticHighlightMesh);
      if (pulseHighlightMesh) mesh.add(pulseHighlightMesh);
      this.root.add(mesh);
      this._disposables.push(...[
        texture,
        staticHighlightTexture,
        pulseHighlightTexture,
        material,
        staticHighlightMaterial,
        pulseHighlightMaterial,
        geometry
      ].filter(Boolean));

      return {
        spec,
        mesh,
        baseMesh,
        baseCanvas: canvas,
        baseContext: ctx,
        baseTexture: texture,
        material,
        baseMaterialColor: material.color.clone(),
        staticHighlightCanvas,
        staticHighlightContext: staticHighlightCanvas?.getContext('2d') || null,
        staticHighlightTexture,
        staticHighlightMaterial,
        pulseHighlightCanvas,
        pulseHighlightContext: pulseHighlightCanvas?.getContext('2d') || null,
        pulseHighlightTexture,
        pulseHighlightMaterial,
        hasStaticHighlight: false,
        hasPulseHighlight: false,
        baseOpacity: spec.opacity,
        idleSpin: spec.idleSpin,
        castSpinActive: false,
        castLocked: false,
        castLit: false,
        snapFrom: 0,
        snapDelta: 0,
        /** 立体态的 z：外圈后退、内圈前突 */
        depthZ: coneDepthZ(spec.radius),
        planeZ: i * FIELD.planeGap
      };
    });
  }

  _buildCastWave() {
    const geometry = new THREE.RingGeometry(
      1 - CAST_RITUAL.waveThickness,
      1 + CAST_RITUAL.waveThickness,
      CAST_RITUAL.waveSegments
    );
    this.castWaveMaterial = new THREE.MeshBasicMaterial({
      color: COLORS.jadeBright,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    this.castWave = new THREE.Mesh(geometry, this.castWaveMaterial);
    this.castWave.visible = false;
    this.castWave.renderOrder = CAST_RITUAL.waveRenderOrder;
    this.root.add(this.castWave);
    this._castWaveLayerIndex = null;
    this._disposables.push(geometry, this.castWaveMaterial);
  }

  _buildScanBand() {
    const radius = FIELD.outerRadius;
    const halfWidth = radius * SCAN_BAND.radialWidth / 2;
    const segmentAngle = SCAN_BAND.angle / SCAN_BAND.trailSegments;
    this.scanBand = new THREE.Group();
    this.scanSegments = Array.from({ length: SCAN_BAND.trailSegments }, (_, index) => {
      const geometry = new THREE.RingGeometry(
        radius - halfWidth,
        radius + halfWidth,
        SCAN_BAND.segments,
        1,
        SCAN_BAND.startAngle + index * segmentAngle,
        segmentAngle
      );
      const material = new THREE.MeshBasicMaterial({
        color: new THREE.Color(COLORS.jadeBright).multiplyScalar(SCAN_BAND.brightness),
        transparent: true,
        opacity: 0,
        depthWrite: false,
        side: THREE.DoubleSide,
        blending: THREE.AdditiveBlending
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.renderOrder = SCAN_BAND.renderOrder;
      this.scanBand.add(mesh);
      this._disposables.push(geometry, material);
      return {
        material,
        opacityScale: Math.pow(
          (index + 1) / SCAN_BAND.trailSegments,
          SCAN_BAND.trailPower
        )
      };
    });
    this.scanBand.renderOrder = SCAN_BAND.renderOrder;
    this.root.add(this.scanBand);
  }

  _buildTaiji() {
    const size = Math.max(256, this.textureSize / 2);
    const canvas = makeCanvas(size, this.canvasFactory);
    const ctx = canvas.getContext('2d');
    drawTaiji({ ctx, size, radius: 0.36 });

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    this.taijiMaterial = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
      opacity: 0,
      color: new THREE.Color(COLORS.taiji).multiplyScalar(TAIJI_STYLE.materialBrightness)
    });
    const world = FIELD.outerRadius * TAIJI_RADIUS * 2 / 0.72; // 贴图里太极占 0.36 半径
    this.taiji = new THREE.Mesh(new THREE.PlaneGeometry(world, world), this.taijiMaterial);
    this.taiji.renderOrder = TAIJI_STYLE.renderOrder;
    this.root.add(this.taiji);
    this._disposables.push(texture, this.taijiMaterial, this.taiji.geometry);
  }

  _buildParticles(count) {
    const positions = new Float32Array(count * 3);
    this._particleState = new Float32Array(count * 4); // r, angle, omega, zBase

    for (let i = 0; i < count; i += 1) {
      const r = THREE.MathUtils.lerp(PARTICLES.radiusMin, PARTICLES.radiusMax, Math.random());
      const a = Math.random() * Math.PI * 2;
      const w = THREE.MathUtils.lerp(PARTICLES.angularMin, PARTICLES.angularMax, Math.random()) *
        (Math.random() < 0.5 ? -1 : 1);
      const z = (Math.random() - 0.5) * 2 * PARTICLES.zJitter;
      this._particleState.set([r, a, w, z], i * 4);
      positions.set([Math.cos(a) * r, Math.sin(a) * r, z], i * 3);
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.particleMaterial = new THREE.PointsMaterial({
      color: new THREE.Color(COLORS.jadeBright),
      size: PARTICLES.size,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true
    });
    this.particles = new THREE.Points(geo, this.particleMaterial);
    this.particles.renderOrder = 20;
    this.root.add(this.particles);
    this._disposables.push(geo, this.particleMaterial);
  }

  _buildStars(count) {
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i += 1) {
      positions.set(
        [
          (Math.random() - 0.5) * STARS.spread,
          (Math.random() - 0.5) * STARS.spread,
          THREE.MathUtils.lerp(STARS.depth[0], STARS.depth[1], Math.random())
        ],
        i * 3
      );
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.starMaterial = new THREE.PointsMaterial({
      color: new THREE.Color(COLORS.star),
      size: STARS.size,
      sizeAttenuation: false,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this.stars = new THREE.Points(geo, this.starMaterial);
    this.stars.renderOrder = 0;
    this.root.add(this.stars);
    this._disposables.push(geo, this.starMaterial);
  }

  _buildVortex() {
    const r = FIELD.outerRadius * VORTEX.radiusScale;
    const geo = new THREE.TorusGeometry(r, VORTEX.tube, 8, VORTEX.segments);
    this.vortexMaterial = new THREE.MeshBasicMaterial({
      color: new THREE.Color(COLORS.gold).multiplyScalar(VORTEX.brightness),
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this.vortex = new THREE.Mesh(geo, this.vortexMaterial);
    this.vortex.renderOrder = 40;
    this.root.add(this.vortex);
    this._disposables.push(geo, this.vortexMaterial);
  }

  /* ------------------------------------------------------- 更新 */

  /**
   * @param {number} dt 秒
   */
  update(dt) {
    this._t += dt;
    this._applyOrientation();
    const c = this.control;
    const meihuaMode = this.mode === 'meihua';
    const modeScale = meihuaMode ? MEIHUA_VIEW.scale : 1;

    // --- 中心扩散辉光（Sprite 始终面向相机） ---
    const centerGlowLevel = Math.max(c.expand, c.awaken * CENTER_GLOW.awakenWeight);
    const centerGlowPulse = CENTER_GLOW.pulseBase
      + CENTER_GLOW.pulseAmplitude * Math.sin(this._t * CENTER_GLOW.pulseSpeed);
    const centerGlowVortex = 1 + c.vortex * CENTER_GLOW.vortexOpacityBoost;
    this.centerGlowMaterial.opacity = centerGlowLevel * CENTER_GLOW.opacity
      * centerGlowPulse * centerGlowVortex
      * (1 + (c.completionPulse + c.taijiFlash) * CAST_RITUAL.glowFlashBoost);
    if (meihuaMode) this.centerGlowMaterial.opacity *= MEIHUA_VIEW.glowOpacityScale;
    const centerGlowScale = 1 + c.vortex * CENTER_GLOW.vortexScaleBoost;
    this.centerGlow.scale.set(
      this._centerGlowDiameter * centerGlowScale * modeScale,
      this._centerGlowDiameter * centerGlowScale * modeScale,
      1
    );

    // --- 十二层罗盘环 + 数据高亮层 ---
    this.layers.forEach((layer, i) => {
      const reveal = c.layerReveal[i];
      const modeVisible = (!meihuaMode || MEIHUA_VIEW.layerKeys.includes(layer.spec.key))
        && !this._qualityHiddenLayers.has(layer.spec.key);
      layer.mesh.visible = modeVisible;
      // 全盘先以 ringBaseOpacity 低亮度出现；落爻层再由 reveal 单独提亮。
      // 几何始终是完整环，不再从阵眼缩放长出。
      const k = Math.max(c.ringBaseOpacity, reveal);
      layer.material.opacity = (modeVisible ? 1 : 0) * layer.baseOpacity * RING_STYLE.opacityScale * k
        * (1 + c.vortex * RING_STYLE.vortexOpacityBoost);
      const movingLinePulse = RING_STYLE.movingPulseBase
        + RING_STYLE.movingPulseAmplitude
          * Math.sin(this._t * RING_STYLE.movingPulseSpeed + i);
      if (this.movingLayers.has(i)) {
        layer.material.color.copy(this._movingRingColor).multiplyScalar(movingLinePulse);
      } else if (layer.castSpinActive || layer.castLit) {
        layer.material.color.copy(this._castRingColor).multiplyScalar(movingLinePulse);
      } else {
        const restingColor = layer.spec.preserveTextureColors
          ? layer.baseMaterialColor
          : this._staticRingColor;
        layer.material.color.lerp(restingColor, RING_STYLE.colorLerp);
      }
      layer.material.uniforms.uOpacity.value = layer.material.opacity;

      const highlightReveal = c.highlightReveal[i] || 0;
      if (layer.staticHighlightMaterial) {
        layer.staticHighlightMaterial.opacity = layer.hasStaticHighlight
          ? highlightReveal * k * CELL_HIGHLIGHT.opacity
          : 0;
      }
      const movingPulse = CELL_HIGHLIGHT.pulseBase
        + CELL_HIGHLIGHT.pulseAmplitude * Math.sin(this._t * CELL_HIGHLIGHT.pulseSpeed + i);
      if (layer.pulseHighlightMaterial) {
        layer.pulseHighlightMaterial.opacity = layer.hasPulseHighlight
          ? highlightReveal * k * CELL_HIGHLIGHT.pulseOpacity * movingPulse
          : 0;
      }

      if (this._ringSnapActive) {
        layer.mesh.rotation.z = layer.snapFrom + layer.snapDelta * c.ringSnap;
      } else if (!this._ringsSnapped && !layer.castLocked) {
        const direction = Math.sign(layer.idleSpin) || 1;
        const speed = layer.castSpinActive
          ? CAST_RITUAL.angularSpeed * direction
          : layer.idleSpin * c.spinMultiplier;
        // 落爻层的 angularSpeed 是实际角速度，不再叠乘背景状态倍率；
        // 否则 casting=2.5 会把指定的 5 rad/s 错放大为 12.5 rad/s。
        layer.mesh.rotation.z += speed * dt;
      }
      layer.mesh.position.z = THREE.MathUtils.lerp(layer.planeZ, layer.depthZ, c.depth);

      const completionScale = THREE.MathUtils.lerp(1, CAST_RITUAL.completionScale, c.completionPulse);
      layer.mesh.scale.setScalar(modeScale * c.layerScale[i] * completionScale);
    });

    if (this._ringSnapActive && c.ringSnap >= 1) {
      this.layers.forEach(({ mesh }) => { mesh.rotation.z = 0; });
      this._ringSnapActive = false;
      this._ringsSnapped = true;
    }

    // --- 每爻定格时从对应层向外扩散的一次性环波 ---
    if (this.castWave.visible && this._castWaveLayerIndex !== null) {
      const layer = this.layers[this._castWaveLayerIndex];
      const progress = THREE.MathUtils.clamp(c.castWave, 0, 1);
      const radius = FIELD.outerRadius * layer.spec.radius
        * THREE.MathUtils.lerp(1, 1 + CAST_RITUAL.waveExpansion, progress);
      this.castWave.scale.setScalar(radius);
      this.castWave.position.z = THREE.MathUtils.lerp(layer.planeZ, layer.depthZ, c.depth)
        + CAST_RITUAL.waveDepthOffset;
      this.castWaveMaterial.opacity = (1 - progress) * CAST_RITUAL.waveOpacity;
    }

    // --- 最外圈扫描光带 ---
    const outerLayer = this.layers.at(-1);
    this.scanBand.visible = !meihuaMode && this.performanceTier < 2;
    this.scanSegments.forEach(({ material, opacityScale }) => {
      material.opacity = (meihuaMode ? 0 : 1) * c.expand * SCAN_BAND.opacity * opacityScale;
    });
    this.scanBand.rotation.z += (Math.PI * 2 / SCAN_BAND.period) * dt;
    this.scanBand.position.z = THREE.MathUtils.lerp(outerLayer.planeZ, outerLayer.depthZ, c.depth)
      + SCAN_BAND.depthOffset;
    this.scanBand.scale.setScalar(THREE.MathUtils.lerp(RING_STYLE.revealScaleMin, 1, c.expand));

    // --- 太极 ---
    const taijiGlow = Math.max(c.awaken, c.expand);
    const taijiPulse = TAIJI_STYLE.opacityBase
      + TAIJI_STYLE.opacityPulse * Math.sin(this._t * TAIJI_STYLE.pulseSpeed);
    // NormalBlending 的 alpha 必须留在 [0, 1]。旋涡增益曾把它推到
    // 约 1.10，在 EffectComposer 的浮点渲染目标中会产生纯黑圆盘。
    this.taijiMaterial.opacity = THREE.MathUtils.clamp(
      taijiGlow * taijiPulse * (1 + c.vortex * TAIJI_STYLE.vortexOpacityBoost),
      0,
      1
    );
    this.taiji.position.z = THREE.MathUtils.lerp(0, coneDepthZ(0), c.depth);
    this.taiji.scale.setScalar(
      THREE.MathUtils.lerp(TAIJI_STYLE.minScale, 1, taijiGlow)
      * (1 + c.vortex * TAIJI_STYLE.vortexScaleBoost)
      * (1 + c.completionPulse * CAST_RITUAL.taijiFlashScale)
      * (1 + c.taijiFlash * CAST_RITUAL.taijiFlashScale)
      * modeScale
    );

    // --- 粒子 ---
    this.particleMaterial.opacity = (PARTICLES.opacity + c.vortex * PARTICLES.vortexOpacityBoost)
      * c.expand * (meihuaMode ? MEIHUA_VIEW.particleOpacityScale : 1);
    const pos = this.particles.geometry.attributes.position.array;
    const st = this._particleState;
    const n = Math.min(pos.length / 3, this.particles.geometry.drawRange.count);
    for (let i = 0; i < n; i += 1) {
      const o = i * 4;
      let r = st[o];
      let a = st[o + 1] + st[o + 2] * dt
        * (1 + c.vortex * PARTICLES.vortexAngularBoost);
      // 立体态时向中心汇聚，形成漏斗里的气机流转
      r -= PARTICLES.inflow * dt * (c.depth + c.vortex * PARTICLES.vortexInflowBoost);
      if (r < PARTICLES.radiusMin * PARTICLES.resetRadiusScale) r = PARTICLES.radiusMax;
      st[o] = r;
      st[o + 1] = a;
      const normalizedRadius = r / FIELD.outerRadius;
      const zTarget = st[o + 3] + c.depth * coneDepthZ(normalizedRadius);
      pos[i * 3] = Math.cos(a) * r;
      pos[i * 3 + 1] = Math.sin(a) * r;
      pos[i * 3 + 2] = zTarget;
    }
    this.particles.geometry.attributes.position.needsUpdate = true;

    // --- 旋涡 ---
    this.vortex.visible = !meihuaMode;
    this.vortexMaterial.opacity = (meihuaMode ? 0 : 1) * c.vortex * VORTEX.opacity;
    const shock = 1 + c.shock * (VORTEX.shockwave - 1);
    this.vortex.scale.setScalar(THREE.MathUtils.lerp(0.85, 1, c.vortex) * shock);
    this.vortex.rotation.z += VORTEX.rotationSpeed * dt * c.vortex;

    // --- 星场视差 ---
    this.stars.rotation.z += STARS.parallax * dt;
    this.starMaterial.opacity = 0.35 + 0.3 * (1 - c.expand * 0.5);
  }

  /* ------------------------------------------------------- 接口 */

  /** 标记哪几层是动爻（层序 0–5 对应初爻到上爻） */
  setMovingLines(positions = []) {
    const layerIndices = positions.map((position) => {
      const key = CAST_RITUAL.layerKeys[position - 1];
      return this.layers.findIndex(({ spec }) => spec.key === key);
    }).filter((index) => index >= 0);
    this.movingLayers = new Set(layerIndices);
  }

  /** 第 n 爻开始时，让已存在的响应层提亮并切入高速旋转。 */
  beginCastLayer(position) {
    const key = CAST_RITUAL.layerKeys[position - 1];
    const index = this.layers.findIndex(({ spec }) => spec.key === key);
    const layer = this.layers[index];
    if (!layer) return -1;
    this._ringSnapActive = false;
    this._ringsSnapped = false;
    this.control.ringSnap = 0;
    layer.castSpinActive = true;
    layer.castLocked = false;
    layer.castLit = true;
    return index;
  }

  /** 展开结束急停，并在该层半径触发一次青白/赤色扩散环。 */
  settleCastLayer(index, moving = false) {
    const layer = this.layers[index];
    if (!layer) return false;
    layer.castSpinActive = false;
    layer.castLocked = true;
    layer.castLit = true;
    this._castWaveLayerIndex = index;
    this.control.castWave = 0;
    this.castWaveMaterial.color.set(moving ? COLORS.crimson : COLORS.jadeBright);
    this.castWave.visible = true;
    return true;
  }

  finishCastWave() {
    this.control.castWave = 1;
    this.castWaveMaterial.opacity = 0;
    this.castWave.visible = false;
    this._castWaveLayerIndex = null;
  }

  /** 捕获各层当前角度，供 Director 在 0.8 秒内按最短路径归零。 */
  beginSnapToPosition() {
    this.layers.forEach((layer) => {
      layer.castSpinActive = false;
      layer.castLocked = false;
      layer.castLit = false;
      layer.snapFrom = Math.atan2(
        Math.sin(layer.mesh.rotation.z),
        Math.cos(layer.mesh.rotation.z)
      );
      layer.snapDelta = -layer.snapFrom;
    });
    this.control.ringSnap = 0;
    this._ringSnapActive = true;
    this._ringsSnapped = false;
  }

  /** 离开成卦态后恢复相邻反向的空闲旋转。 */
  resumeIdleSpin(resetRotation = false) {
    this._ringSnapActive = false;
    this._ringsSnapped = false;
    this.control.ringSnap = 0;
    this.layers.forEach((layer) => {
      layer.castSpinActive = false;
      layer.castLocked = false;
      layer.castLit = false;
      if (resetRotation) layer.mesh.rotation.z = 0;
    });
  }

  get ringsSnapped() { return Boolean(this._ringsSnapped); }

  /**
   * 把独立飞星模块的排盘事实重绘到九星环。
   * 此接口只消费 scene/feixing-view.js 的展示模型，不接触六爻 result。
   */
  setFlyingStars(model) {
    const layer = this.layers.find(({ spec }) => spec.key === 'nineStars');
    if (!layer || !model?.ringTokens || !model?.center) return false;

    const colorForTone = (tone) => new THREE.Color(
      tone === 'crimson'
        ? COLORS.crimson
        : tone === 'gold' ? COLORS.gold : COLORS.jadeBright
    ).getStyle(THREE.SRGBColorSpace);
    const tokens = model.ringTokens.map((token) => ({
      ...token,
      color: colorForTone(token.tone)
    }));
    layer.spec.tokens = tokens;
    layer.baseContext.clearRect(0, 0, this.textureSize, this.textureSize);
    drawRing({
      ctx: layer.baseContext,
      size: this.textureSize,
      tokens,
      inner: layer.spec.inner,
      outer: layer.spec.outer,
      offset: layer.spec.offset,
      fontScale: layer.spec.fontScale,
      textOpacity: layer.spec.textOpacity,
      weight: layer.spec.weight,
      color: this._staticRingColor.getStyle(THREE.SRGBColorSpace),
      glow: this.textureSize * RING_STYLE.glowRatio
    });
    layer.baseTexture.needsUpdate = true;

    this._flyingStarInteractiveCells = tokens.map((token) => ({
      layerKey: 'nineStars',
      token: token.label,
      tooltip: token.tooltip,
      confidence: null
    }));

    this.flyingStarModel = model;
    return true;
  }

  /**
   * 重绘成卦后的格子高亮。cellMap 以 RING_SPECS.key 为键：
   * { branches: { items: [...], connections: [...] }, ... }
   */
  highlightCells(cellMap = {}) {
    this._lastCellMap = cellMap;
    const highlighted = [];
    this._interactiveCells = [];
    for (let index = 0; index < this.layers.length; index += 1) {
      const layer = this.layers[index];
      const entry = cellMap[layer.spec.key] || {};
      const items = entry.items || [];
      const connections = entry.connections || [];
      const staticItems = items.filter(({ pulse }) => !pulse);
      const pulseItems = items.filter(({ pulse }) => pulse);
      for (const item of items) {
        if (!item.tooltip) continue;
        this._interactiveCells.push({
          layerKey: layer.spec.key,
          token: tokenLabel(item.token),
          tooltip: item.tooltip,
          confidence: item.confidence ?? null
        });
      }
      const prepareItems = (source) => source.map((item) => ({
        ...item,
        color: new THREE.Color(
          CELL_HIGHLIGHT.colors[item.kind] ?? CELL_HIGHLIGHT.colors.normal
        ).getStyle(THREE.SRGBColorSpace),
        boost: item.boost ?? (item.kind === 'useGod' ? CELL_HIGHLIGHT.useGodBoost : 1),
        dashed: item.kind === 'void' || item.dashed,
        dash: CELL_HIGHLIGHT.voidDash,
        fillOpacity: CELL_HIGHLIGHT.fillOpacity,
        strokeOpacity: CELL_HIGHLIGHT.strokeOpacity,
        textOpacity: CELL_HIGHLIGHT.textOpacity,
        lineWidthScale: CELL_HIGHLIGHT.lineWidthScale
      }));
      const prepareConnections = (source) => source.map((connection) => ({
        ...connection,
        color: new THREE.Color(
          CELL_HIGHLIGHT.colors[connection.kind] ?? CELL_HIGHLIGHT.colors.changed
        ).getStyle(THREE.SRGBColorSpace),
        inset: connection.inset
          ?? CELL_HIGHLIGHT.connectionInsets[connection.kind]
          ?? CELL_HIGHLIGHT.connectionInset,
        opacity: connection.opacity ?? CELL_HIGHLIGHT.connectionOpacity,
        lineWidthScale: connection.lineWidthScale ?? CELL_HIGHLIGHT.lineWidthScale
      }));

      for (const context of [layer.staticHighlightContext, layer.pulseHighlightContext].filter(Boolean)) {
        context.clearRect(0, 0, this.highlightTextureSize, this.highlightTextureSize);
      }
      if (layer.staticHighlightContext) {
        drawRingHighlights({
          ctx: layer.staticHighlightContext,
          size: this.highlightTextureSize,
          spec: layer.spec,
          items: prepareItems(staticItems),
          connections: prepareConnections(connections)
        });
      }
      if (layer.pulseHighlightContext) {
        drawRingHighlights({
          ctx: layer.pulseHighlightContext,
          size: this.highlightTextureSize,
          spec: layer.spec,
          items: prepareItems(pulseItems)
        });
      }
      layer.hasStaticHighlight = Boolean(layer.staticHighlightContext)
        && (staticItems.length > 0 || connections.length > 0);
      layer.hasPulseHighlight = Boolean(layer.pulseHighlightContext) && pulseItems.length > 0;
      if (layer.staticHighlightTexture) layer.staticHighlightTexture.needsUpdate = true;
      if (layer.pulseHighlightTexture) layer.pulseHighlightTexture.needsUpdate = true;
      if (layer.hasStaticHighlight || layer.hasPulseHighlight) highlighted.push(index);
    }
    return highlighted;
  }

  /** 六爻显示全部环；梅花只显示内圈八卦环并放大。 */
  setMode(mode = 'liuyao') {
    this.mode = mode === 'meihua' ? 'meihua' : 'liuyao';
  }

  /**
   * 移动端视觉降级：只改贴图、可见层和粒子数，不触碰 result。
   * tier 0=完整，1=低分辨率+半粒子，2=八层+无扫描带。
   */
  setPerformanceTier(tier = 0, { layerKeys = [], textureSize = this.textureSize } = {}) {
    this.performanceTier = Math.max(0, Math.min(2, Math.trunc(tier)));
    this._qualityHiddenLayers = this.performanceTier >= 2
      ? new Set(this.layers.map(({ spec }) => spec.key).filter((key) => !layerKeys.includes(key)))
      : new Set();
    const particleCount = this.particles.geometry.attributes.position.count;
    const fraction = this.performanceTier === 0 ? 1 : (this.performanceTier === 1 ? 0.5 : 0.35);
    this.particles.geometry.setDrawRange(0, Math.floor(particleCount * fraction));
    if (textureSize !== this.textureSize) this._resizeRingTextures(textureSize);
    return this.performanceTier;
  }

  _resizeRingTextures(textureSize) {
    const size = Math.max(128, Math.round(textureSize));
    this.textureSize = size;
    this.highlightTextureSize = Math.round(size * FIELD.highlightTextureScale);
    for (const layer of this.layers) {
      layer.baseCanvas.width = size;
      layer.baseCanvas.height = size;
      drawRing({
        ctx: layer.baseContext,
        size,
        tokens: layer.spec.tokens,
        inner: layer.spec.inner,
        outer: layer.spec.outer,
        offset: layer.spec.offset,
        fontScale: layer.spec.fontScale,
        textOpacity: layer.spec.textOpacity,
        weight: layer.spec.weight,
        color: this._staticRingColor.getStyle(THREE.SRGBColorSpace),
        glow: size * RING_STYLE.glowRatio
      });
      layer.baseTexture.needsUpdate = true;
      for (const canvas of [layer.staticHighlightCanvas, layer.pulseHighlightCanvas].filter(Boolean)) {
        canvas.width = this.highlightTextureSize;
        canvas.height = this.highlightTextureSize;
      }
    }
    this.highlightCells(this._lastCellMap);
  }

  /**
   * 射线命中带 tooltip 的高亮格。只做几何拾取，不解释候选吉凶。
   */
  pickInteractiveCell(clientX, clientY, camera, canvasRect) {
    if (
      !this._interactiveCells.length
      && !this._flyingStarInteractiveCells.length
    ) return null;
    this._pointer.set(
      ((clientX - canvasRect.left) / canvasRect.width) * 2 - 1,
      -((clientY - canvasRect.top) / canvasRect.height) * 2 + 1
    );
    this._raycaster.setFromCamera(this._pointer, camera);

    for (const entry of [...this._interactiveCells, ...this._flyingStarInteractiveCells]) {
      const layer = this.layers.find(({ spec }) => spec.key === entry.layerKey);
      if (!layer?.mesh.visible) continue;
      const hit = this._raycaster.intersectObject(layer.baseMesh, false)[0];
      if (!hit) continue;
      const point = layer.mesh.worldToLocal(hit.point.clone());
      const worldSize = layer.baseMesh.geometry.parameters.width;
      const radius = Math.hypot(point.x, point.y) / worldSize;
      if (radius < layer.spec.inner || radius > layer.spec.outer) continue;

      const step = (Math.PI * 2) / layer.spec.tokens.length;
      const angle = Math.atan2(-point.y, point.x);
      const relative = ((angle - layer.spec.offset) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
      const index = Math.round(relative / step) % layer.spec.tokens.length;
      const token = tokenLabel(layer.spec.tokens[index]);
      if (token === entry.token) return entry;
    }
    return null;
  }

  /** 掌心控向：传入相对默认视角的偏转；仅在 depth > 0 时实际生效。 */
  setOrientation({ x = 0, y = 0, z = 0 }) {
    this._orientation = { x, y, z };
    this._applyOrientation();
  }

  /** AIM 指尖跟随：只平移阵眼/整盘，不改变深度与转向。 */
  setAimPosition({ x = 0, y = 0 }) {
    this.root.position.x = x;
    this.root.position.y = y;
  }

  _applyOrientation() {
    const t = this._t || 0;
    const phase = t * Math.PI * 2 / CAMERA.restDrift.period;
    const orientation = this._orientation || { x: 0, y: 0, z: 0 };
    // restTilt、手势控向和呼吸漂移只属于立体态。depth=0 时严格正对屏幕；
    // 成卦展开过程中随锥形深度平滑进入，避免突然跳角度。
    const depth = THREE.MathUtils.clamp(this.control?.depth ?? 0, 0, 1);
    this.root.rotation.x = depth * (
      CAMERA.restTilt.x + orientation.x
      + Math.sin(phase + CAMERA.restDrift.phaseX) * CAMERA.restDrift.amplitudeX
    );
    this.root.rotation.y = depth * (
      CAMERA.restTilt.y + orientation.y
      + Math.sin(phase + CAMERA.restDrift.phaseY) * CAMERA.restDrift.amplitudeY
    );
    this.root.rotation.z = depth * (CAMERA.restTilt.z + orientation.z);
  }

  dispose() {
    for (const d of this._disposables) d.dispose?.();
    this.root.clear();
  }
}
